import { paiseToLakhs } from "./ctcLakhs";
import { buildEducationSummary } from "./educationCatalog";
import type { EnglishLevel, Gender } from "./formCatalogs";
import type { IntakeExistingProfile, IntakeResumeParseResponse } from "../types/api";

export type CandidateFormValues = {
  full_name: string;
  email: string;
  cities: string[];
  current_title: string;
  education_summary: string;
  gender: string | null;
  whatsapp_opt_in: boolean;
  education_level: string | null;
  college_name: string;
  degree: string | null;
  specialization: string;
  graduation_year: number | undefined;
  work_status: "working" | "fresher";
  years_experience: number | undefined;
  current_ctc_lakhs: number | undefined;
  expected_ctc_lakhs: number | undefined;
  availability_in_days: number | undefined;
  linkedin_url: string;
  skills: string[];
  languages: string[];
  segments: string[];
  english_level: string | null;
  consent_given: boolean;
  role_family_code: string | null;
};

export type RecruiterCandidateFormValues = CandidateFormValues & {
  phone: string;
  notes: string;
};

export const CANDIDATE_FORM_INITIAL: CandidateFormValues = {
  full_name: "",
  email: "",
  cities: [],
  current_title: "",
  education_summary: "",
  gender: null,
  whatsapp_opt_in: true,
  education_level: null,
  college_name: "",
  degree: null,
  specialization: "",
  graduation_year: undefined,
  work_status: "working",
  years_experience: undefined,
  current_ctc_lakhs: undefined,
  expected_ctc_lakhs: undefined,
  availability_in_days: undefined,
  linkedin_url: "",
  skills: [],
  languages: [],
  segments: [],
  english_level: null,
  consent_given: false,
  role_family_code: null,
};

export const RECRUITER_CANDIDATE_FORM_INITIAL: RecruiterCandidateFormValues = {
  ...CANDIDATE_FORM_INITIAL,
  phone: "",
  notes: "",
  whatsapp_opt_in: false,
};

export const GRADUATION_YEAR_MIN = 1950;
export const GRADUATION_YEAR_MAX_OFFSET = 4;

export function valuesFromExistingProfile(existing: IntakeExistingProfile): CandidateFormValues {
  return {
    ...CANDIDATE_FORM_INITIAL,
    full_name: existing.full_name ?? "",
    email: existing.email ?? "",
    cities: existing.cities ?? (existing.city ? [existing.city] : []),
    current_title: existing.current_title ?? "",
    education_summary: existing.education_summary ?? "",
    linkedin_url: existing.linkedin_url ?? "",
    years_experience: existing.years_experience ?? undefined,
    current_ctc_lakhs:
      existing.current_ctc_paise != null ? paiseToLakhs(existing.current_ctc_paise) : undefined,
    expected_ctc_lakhs:
      existing.expected_ctc_paise != null ? paiseToLakhs(existing.expected_ctc_paise) : undefined,
    availability_in_days: existing.availability_in_days ?? undefined,
    skills: existing.skills ?? [],
    languages: existing.languages ?? [],
    segments: existing.segments ?? [],
    gender: existing.gender ?? null,
    work_status: existing.work_status === "fresher" ? "fresher" : "working",
    education_level: existing.education_level ?? null,
    college_name: existing.college_name ?? "",
    degree: existing.degree ?? null,
    specialization: existing.specialization ?? "",
    graduation_year: existing.graduation_year ?? undefined,
    english_level: existing.english_level ?? null,
    whatsapp_opt_in: existing.whatsapp_opt_in ?? true,
    consent_given: false,
    role_family_code: existing.role_family_code ?? null,
  };
}

const GENDER_VALUES: Gender[] = ["male", "female", "prefer_not_to_say"];
const ENGLISH_LEVEL_VALUES: EnglishLevel[] = ["none", "basic", "intermediate", "advanced"];

export function optionalGender(value: string | null | undefined): Gender | undefined {
  return GENDER_VALUES.includes(value as Gender) ? (value as Gender) : undefined;
}

export function optionalEnglishLevel(value: string | null | undefined): EnglishLevel | undefined {
  return ENGLISH_LEVEL_VALUES.includes(value as EnglishLevel) ? (value as EnglishLevel) : undefined;
}

export function educationSummaryFromForm(values: CandidateFormValues): string {
  return (
    buildEducationSummary({
      education_level: values.education_level,
      degree: values.degree,
      specialization: values.specialization,
      college_name: values.college_name,
      graduation_year: values.graduation_year,
    }) || values.education_summary
  );
}

export function mergeResumeParse(
  values: CandidateFormValues,
  parsed: IntakeResumeParseResponse,
  resolveCity: (raw: string) => string | null,
  skillLabels?: Record<string, string>,
): { next: Partial<CandidateFormValues>; parsedKeys: Set<string> } {
  const next: Partial<CandidateFormValues> = {};
  const parsedKeys = new Set<string>();
  if (parsed.full_name && !values.full_name.trim()) {
    next.full_name = parsed.full_name;
    parsedKeys.add("full_name");
  }
  if (parsed.email && !values.email.trim()) {
    next.email = parsed.email;
    parsedKeys.add("email");
  }
  if (parsed.city && values.cities.length === 0) {
    const resolved = resolveCity(parsed.city);
    next.cities = resolved ? [resolved] : [parsed.city];
    parsedKeys.add("cities");
  }
  if (parsed.current_title && !values.current_title.trim()) {
    next.current_title = parsed.current_title;
    parsedKeys.add("current_title");
  }
  if (parsed.education_summary && !values.education_summary.trim()) {
    next.education_summary = parsed.education_summary;
    parsedKeys.add("education_summary");
  }
  if (parsed.education_level) {
    next.education_level = parsed.education_level;
    parsedKeys.add("education_level");
  }
  if (parsed.college_name) {
    next.college_name = parsed.college_name;
    parsedKeys.add("college_name");
  }
  if (parsed.degree) {
    next.degree = parsed.degree;
    parsedKeys.add("degree");
  }
  if (parsed.specialization) {
    next.specialization = parsed.specialization;
    parsedKeys.add("specialization");
  }
  if (parsed.graduation_year) {
    next.graduation_year = parsed.graduation_year;
    parsedKeys.add("graduation_year");
  }
  if (parsed.linkedin_url && !values.linkedin_url.trim()) {
    next.linkedin_url = parsed.linkedin_url;
    parsedKeys.add("linkedin_url");
  }
  if (parsed.availability_in_days != null && values.availability_in_days == null) {
    next.availability_in_days = parsed.availability_in_days;
    parsedKeys.add("availability_in_days");
  }
  if (parsed.years_experience != null && values.years_experience == null) {
    next.years_experience = parsed.years_experience;
    parsedKeys.add("years_experience");
    next.work_status = parsed.years_experience === 0 ? "fresher" : "working";
    parsedKeys.add("work_status");
  }
  if (parsed.work_status) {
    next.work_status = parsed.work_status === "fresher" ? "fresher" : "working";
    parsedKeys.add("work_status");
  }
  if (parsed.gender) {
    next.gender = parsed.gender;
    parsedKeys.add("gender");
  }
  if (parsed.english_level) {
    next.english_level = parsed.english_level;
    parsedKeys.add("english_level");
  }
  if (parsed.current_ctc_lakhs != null && values.current_ctc_lakhs == null) {
    next.current_ctc_lakhs = parsed.current_ctc_lakhs;
    parsedKeys.add("current_ctc_lakhs");
  }
  if (parsed.expected_ctc_lakhs != null && values.expected_ctc_lakhs == null) {
    next.expected_ctc_lakhs = parsed.expected_ctc_lakhs;
    parsedKeys.add("expected_ctc_lakhs");
  }
  if (parsed.skills?.length) {
    const existing = new Set(values.skills.map((s) => s.toLowerCase()));
    const merged = [...values.skills];
    for (const skill of parsed.skills) {
      const label = skillLabels?.[skill] ?? skill;
      const key = String(label).toLowerCase();
      if (!existing.has(key)) {
        existing.add(key);
        merged.push(String(label));
      }
    }
    next.skills = merged;
    parsedKeys.add("skills");
  }
  if (parsed.languages?.length) {
    const existing = new Set(values.languages.map((s) => s.toLowerCase()));
    const merged = [...values.languages];
    for (const lang of parsed.languages) {
      if (!existing.has(lang.toLowerCase())) merged.push(lang);
    }
    next.languages = merged;
    parsedKeys.add("languages");
  }
  if (parsed.role_family_code && !values.role_family_code) {
    next.role_family_code = parsed.role_family_code;
    parsedKeys.add("role_family_code");
  }
  return { next, parsedKeys };
}
